import { type FC, type SyntheticEvent, useCallback, useRef, useState } from "react";
import cn from "classnames";
import ReactCrop, { centerCrop, convertToPixelCrop, type Crop, makeAspectCrop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Ref } from "semantic-ui-react";

import styles from "./imageCropper.module.scss";

import { default as imageUtils } from "utils/imageUtils";
import CroppedImagePreview from "./croppedImagePreview/CroppedImagePreview";
import ImageAspectRatio from "enums/imageAspectRatio";

interface ImageCropperProps {
  file: string;
  onCroppedImageChange: (blob: Blob) => void;
  aspectRatio: ImageAspectRatio;
}

const ImageCropper: FC<ImageCropperProps> = (props) => {
  const { aspectRatio = ImageAspectRatio.HD_16X9, file, onCroppedImageChange } = props;
  const [crop, setCrop] = useState<Crop>();
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [cropperVisible, setCropperVisible] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const cropper = useRef<HTMLElement | null>(null);

  const makeClientCrop = useCallback(
    async (crop: PixelCrop) => {
      if (crop.width && crop.height && imgRef.current) {
        const croppedImage = await imageUtils.getCroppedImg(imgRef.current, crop, "newFile", "");
        setCroppedImageUrl(croppedImage.newFileUrl);
        onCroppedImageChange(croppedImage.blob);
      }
    },
    [onCroppedImageChange],
  );

  const handleCropperBlur = useCallback((): void => {
    setCropperVisible(false);
  }, []);

  const handlePreviewClick = useCallback((): void => {
    setCropperVisible(true);
    setTimeout(() => cropper.current?.focus?.());
  }, []);

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const crop = centerAspectCrop(naturalWidth, naturalHeight, aspectRatio);
    setCrop(crop);
    makeClientCrop(convertToPixelCrop(crop, naturalWidth, naturalHeight));
  };

  const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
      makeAspectCrop({ unit: "%", width: 100 }, aspect, mediaWidth, mediaHeight),
      mediaWidth,
      mediaHeight,
    );
  };

  return (
    <div
      className={cn(styles.imageCropper, {
        [styles.cropperActive]: cropperVisible,
      })}
      title="cropper"
    >
      {croppedImageUrl && (
        <div className={styles.previewContainer}>
          <CroppedImagePreview thumbnailUrl={croppedImageUrl} onClick={handlePreviewClick} aspectRatio={aspectRatio} />
        </div>
      )}
      <div className={styles.cropperContainer}>
        <Ref innerRef={cropper}>
          <div tabIndex={-1} className={styles.focusContainer} onBlur={handleCropperBlur}>
            <ReactCrop
              crop={crop}
              aspect={aspectRatio}
              onComplete={makeClientCrop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
            >
              <img ref={imgRef} src={file} alt="Crop me" onLoad={onImageLoad} />
            </ReactCrop>
          </div>
        </Ref>
      </div>
    </div>
  );
};

export default ImageCropper;
